import React, { useState, useEffect, useCallback } from 'react';
import { CHILE_TZ } from '../utils/chileTime';
import { messagesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { EnvelopeSimple, PaperPlaneTilt, ArrowLeft, PencilSimple, Buildings, User, Clock, ArrowBendUpLeft, Checks, Eye, Star, Trash, Warning, MagnifyingGlass, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const PAGE_SIZE = 12;

function MessageRow({ msg, isInbox, onClick, isAdmin, selected, onSelect, onToggleImportant, showDirection }) {
  const name = showDirection
    ? msg.other_name
    : (isInbox ? msg.sender_name : msg.recipient_name);
  const date = msg.created_at ? new Date(msg.created_at) : null;
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 border-b border-[#2B4B8C]/5 transition-colors ${isInbox && !msg.is_read ? 'bg-blue-50/40' : ''} ${selected ? 'bg-[#2BBAA4]/5' : 'hover:bg-[#f8fafc]'}`}
      data-testid={`msg-row-${msg.id}`}
    >
      <Checkbox
        checked={selected}
        onCheckedChange={() => onSelect(msg.id)}
        className="shrink-0 border-[#2B4B8C]/20 data-[state=checked]:bg-[#2BBAA4] data-[state=checked]:border-[#2BBAA4]"
        data-testid={`msg-check-${msg.id}`}
        onClick={(e) => e.stopPropagation()}
      />
      <button
        onClick={(e) => { e.stopPropagation(); onToggleImportant(msg.id); }}
        className="shrink-0 p-0.5 rounded hover:bg-[#2B4B8C]/5 transition-colors"
        data-testid={`msg-star-${msg.id}`}
      >
        <Star size={16} weight={msg.is_important ? 'fill' : 'regular'} className={msg.is_important ? 'text-amber-400' : 'text-[#2B4B8C]/20 hover:text-amber-300'} />
      </button>
      <div onClick={onClick} className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer">
        <div className="w-8 h-8 rounded-full bg-[#2B4B8C]/10 flex items-center justify-center shrink-0">
          <User size={14} className="text-[#2B4B8C]/60" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm ${isInbox && !msg.is_read ? 'font-bold text-[#2B4B8C]' : 'font-medium text-[#2B4B8C]'}`}>{name}</span>
            {showDirection && (
              <Badge className={`text-[9px] border-0 px-1.5 py-0 ${msg.is_inbox ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'}`}>
                {msg.is_inbox ? 'Recibido' : 'Enviado'}
              </Badge>
            )}
            {msg.company_name && (
              <span className="text-[10px] text-[#2B4B8C]/40 truncate">{msg.company_name}{msg.client_name ? ` / ${msg.client_name}` : ''}</span>
            )}
          </div>
          <p className={`text-sm truncate ${isInbox && !msg.is_read ? 'font-semibold text-[#2B4B8C]' : 'text-[#2B4B8C]/70'}`}>{msg.subject}</p>
        </div>
        <div className="text-right shrink-0 flex flex-col items-end gap-1">
          <p className="text-[10px] text-[#2B4B8C]/40">{date ? `${date.toLocaleDateString('es-CL', { timeZone: CHILE_TZ })} ${date.toLocaleTimeString('es-CL', { timeZone: CHILE_TZ, hour: '2-digit', minute: '2-digit' })}` : ''}</p>
          {!isInbox && isAdmin && (
            msg.is_read ? (
              <span className="flex items-center gap-1 text-[10px] text-[#2BBAA4]"><Checks size={12} weight="bold" /> Leido</span>
            ) : (
              <span className="flex items-center gap-1 text-[10px] text-[#2B4B8C]/30"><Checks size={12} /> Enviado</span>
            )
          )}
        </div>
        {isInbox && !msg.is_read && <div className="w-2.5 h-2.5 rounded-full bg-[#2BBAA4] shrink-0" />}
      </div>
    </div>
  );
}

function MessageDetail({ msgId, onBack, onReplySent, currentUserId, isAdmin, onToggleImportant, onDelete }) {
  const [thread, setThread] = useState(null);
  const [replying, setReplying] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);

  const loadThread = useCallback(async () => {
    try {
      const res = await messagesApi.getThread(msgId);
      setThread(res.data);
    } catch { toast.error('Error al cargar conversacion'); }
  }, [msgId]);

  useEffect(() => { loadThread(); }, [loadThread]);

  if (!thread) return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2BBAA4]" /></div>;

  const messages = thread.thread || [];
  const firstMsg = messages[0];
  const replyToId = firstMsg?.sender_id === currentUserId ? firstMsg?.recipient_id : firstMsg?.sender_id;
  const replyToName = messages.find(m => m.sender_id !== currentUserId)?.sender_name || '';

  const handleReply = async () => {
    if (!replyBody.trim()) return;
    setSending(true);
    try {
      await messagesApi.send({
        recipient_id: replyToId,
        subject: `Re: ${thread.subject}`,
        body: replyBody.trim(),
      });
      toast.success('Respuesta enviada');
      setReplyBody('');
      setReplying(false);
      loadThread();
      if (onReplySent) onReplySent();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al enviar'); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="message-detail">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="text-[#2B4B8C]/60 hover:text-[#2B4B8C] -ml-2">
          <ArrowLeft size={16} className="mr-1" /> Volver
        </Button>
        <div className="flex items-center gap-1">
          <button onClick={() => onToggleImportant(thread.id)} className="p-1.5 rounded hover:bg-[#2B4B8C]/5 transition-colors" data-testid="detail-star-btn">
            <Star size={18} weight={thread.is_important ? 'fill' : 'regular'} className={thread.is_important ? 'text-amber-400' : 'text-[#2B4B8C]/25 hover:text-amber-300'} />
          </button>
          <button onClick={() => onDelete(thread.id)} className="p-1.5 rounded hover:bg-red-50 text-[#2B4B8C]/25 hover:text-red-500 transition-colors" data-testid="detail-delete-btn">
            <Trash size={18} />
          </button>
        </div>
      </div>

      <h2 className="text-lg font-bold text-[#2B4B8C]">{thread.subject}</h2>

      <div className="space-y-3 max-h-[450px] overflow-y-auto pr-1">
        {messages.map((m) => {
          const isMine = m.sender_id === currentUserId;
          const date = m.created_at ? new Date(m.created_at) : null;
          return (
            <div key={m.id} className={`rounded-xl p-4 border ${isMine ? 'bg-[#2BBAA4]/5 border-[#2BBAA4]/15 ml-6' : 'bg-[#f8fafc] border-[#2B4B8C]/5 mr-6'}`} data-testid={`thread-msg-${m.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${isMine ? 'bg-[#2BBAA4]/20 text-[#2BBAA4]' : 'bg-[#2B4B8C]/10 text-[#2B4B8C]/60'}`}>
                    {m.sender_name?.charAt(0)}
                  </div>
                  <span className="text-xs font-semibold text-[#2B4B8C]">{m.sender_name}</span>
                  {m.sender_role === 'admin' && <Badge className="bg-[#2BBAA4]/10 text-[#2BBAA4] border-0 text-[9px] py-0">Admin</Badge>}
                  {m.sender_role === 'contractor' && <Badge className="bg-[#2B4B8C]/10 text-[#2B4B8C] border-0 text-[9px] py-0">Contratista</Badge>}
                  {m.company_name && <span className="text-[10px] text-[#2B4B8C]/35">{m.company_name}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#2B4B8C]/35">{date ? `${date.toLocaleDateString('es-CL', { timeZone: CHILE_TZ })} ${date.toLocaleTimeString('es-CL', { timeZone: CHILE_TZ, hour: '2-digit', minute: '2-digit' })}` : ''}</span>
                  {isMine && isAdmin && (
                    m.is_read ? (
                      <span className="flex items-center gap-0.5 text-[10px] text-[#2BBAA4]"><Eye size={10} /> Leido</span>
                    ) : (
                      <span className="flex items-center gap-0.5 text-[10px] text-[#2B4B8C]/25"><Checks size={10} /></span>
                    )
                  )}
                </div>
              </div>
              <p className="text-sm text-[#2B4B8C]/80 whitespace-pre-wrap leading-relaxed">{m.body}</p>
            </div>
          );
        })}
      </div>

      {!replying ? (
        <Button onClick={() => setReplying(true)} variant="outline" className="border-[#2B4B8C]/15 text-[#2B4B8C]" data-testid="reply-btn">
          <ArrowBendUpLeft size={16} className="mr-1.5" /> Responder
        </Button>
      ) : (
        <div className="space-y-3 border border-[#2B4B8C]/10 rounded-xl p-4 bg-[#f8fafc]">
          <p className="text-xs font-medium text-[#2B4B8C]/50">Responder a {replyToName}</p>
          <Textarea value={replyBody} onChange={e => setReplyBody(e.target.value)} placeholder="Escribe tu respuesta..." rows={3} className="border-[#2B4B8C]/15 resize-none bg-white" autoFocus data-testid="reply-body" />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => { setReplying(false); setReplyBody(''); }} className="text-[#2B4B8C]/50">Cancelar</Button>
            <Button size="sm" onClick={handleReply} disabled={sending || !replyBody.trim()} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="send-reply-btn">
              <PaperPlaneTilt size={14} className="mr-1" /> {sending ? 'Enviando...' : 'Enviar respuesta'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Pagination({ total, page, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (totalPages <= 1) return null;
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-[#2B4B8C]/5">
      <span className="text-xs text-[#2B4B8C]/40">{from}-{to} de {total}</span>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)} className="h-7 px-2 text-[#2B4B8C]/60 disabled:opacity-30" data-testid="page-prev">
          <ArrowLeft size={14} />
        </Button>
        <span className="text-xs text-[#2B4B8C]/60 min-w-[60px] text-center">{page} / {totalPages}</span>
        <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} className="h-7 px-2 text-[#2B4B8C]/60 disabled:opacity-30" data-testid="page-next">
          <ArrowLeft size={14} className="rotate-180" />
        </Button>
      </div>
    </div>
  );
}

export default function MessagesPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('inbox');
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [important, setImportant] = useState([]);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({ recipient_id: '', subject: '', body: '' });
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const isAdmin = user?.role === 'admin';

  const fetchMessages = useCallback(async () => {
    try {
      const [inboxRes, sentRes, impRes] = await Promise.all([
        messagesApi.getInbox(),
        messagesApi.getSent(),
        messagesApi.getImportant(),
      ]);
      setInbox(inboxRes.data);
      setSent(sentRes.data);
      setImportant(impRes.data);
    } catch { toast.error('Error al cargar mensajes'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const openCompose = async () => {
    try {
      const res = await messagesApi.getContacts();
      setContacts(res.data);
    } catch { toast.error('Error al cargar contactos'); }
    setForm({ recipient_id: '', subject: '', body: '' });
    setComposeOpen(true);
  };

  const handleSend = async () => {
    if (!form.recipient_id || !form.subject.trim() || !form.body.trim()) {
      toast.error('Completa todos los campos'); return;
    }
    setSending(true);
    try {
      await messagesApi.send(form);
      toast.success('Mensaje enviado');
      setComposeOpen(false);
      fetchMessages();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al enviar'); }
    finally { setSending(false); }
  };

  const openMessage = (msg) => {
    setSelectedMsg(msg.id);
    setSelectedIds([]);
    // Mark as read optimistically in inbox
    if (msg.is_read === false) {
      setInbox(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const listMap = { inbox, sent, important };
  const rawList = listMap[tab] || [];
  const q = search.toLowerCase().trim();
  const currentList = q ? rawList.filter(m => {
    const name = (m.sender_name || m.recipient_name || m.other_name || '').toLowerCase();
    const comp = (m.company_name || '').toLowerCase();
    const cli = (m.client_name || '').toLowerCase();
    const subj = (m.subject || '').toLowerCase();
    const body = (m.body || '').toLowerCase();
    return name.includes(q) || comp.includes(q) || cli.includes(q) || subj.includes(q) || body.includes(q);
  }) : rawList;
  const totalPages = Math.max(1, Math.ceil(currentList.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedList = currentList.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const selectAll = () => {
    setSelectedIds(prev => prev.length === pagedList.length ? [] : pagedList.map(m => m.id));
  };

  const handleToggleImportant = (id) => {
    const flip = (m) => m.id === id ? { ...m, is_important: !m.is_important } : m;
    setInbox(prev => prev.map(flip));
    setSent(prev => prev.map(flip));
    setImportant(prev => {
      const existing = prev.find(m => m.id === id);
      if (existing) return prev.filter(m => m.id !== id);
      return prev;
    });
    if (selectedMsg?.id === id) setSelectedMsg(prev => ({ ...prev, is_important: !prev.is_important }));
    messagesApi.toggleImportant(id).then(() => {
      fetchMessages();
    }).catch(() => {
      setInbox(prev => prev.map(flip));
      setSent(prev => prev.map(flip));
      toast.error('Error al actualizar');
    });
  };

  const handleDeleteSingle = (id) => {
    setDeleteConfirm({ type: 'single', ids: [id] });
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setDeleteConfirm({ type: 'bulk', ids: selectedIds });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      if (deleteConfirm.type === 'single') {
        await messagesApi.deleteMessage(deleteConfirm.ids[0]);
      } else {
        const folder = tab === 'important' ? 'inbox' : tab;
        await messagesApi.bulkDelete({ message_ids: deleteConfirm.ids, folder });
      }
      toast.success(deleteConfirm.ids.length === 1 ? 'Mensaje eliminado' : `${deleteConfirm.ids.length} mensajes eliminados`);
      setSelectedIds([]);
      setDeleteConfirm(null);
      if (selectedMsg && deleteConfirm.ids.includes(selectedMsg)) setSelectedMsg(null);
      fetchMessages();
      setPage(1);
    } catch { toast.error('Error al eliminar'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>;

  const unreadCount = inbox.filter(m => !m.is_read).length;
  const importantCount = important.length;

  const renderList = (messages, isInboxTab) => {
    if (messages.length === 0) {
      if (q) {
        return (
          <div className="text-center py-12 text-[#2B4B8C]/40 text-sm">
            <MagnifyingGlass size={32} className="mx-auto mb-2 opacity-30" />
            No se encontraron mensajes para "{search}"
          </div>
        );
      }
      const icons = { inbox: EnvelopeSimple, sent: PaperPlaneTilt, important: Star };
      const texts = { inbox: 'No hay mensajes en tu bandeja', sent: 'No has enviado mensajes', important: 'No hay mensajes importantes' };
      const Icon = icons[tab] || EnvelopeSimple;
      return (
        <div className="text-center py-12 text-[#2B4B8C]/40 text-sm">
          <Icon size={32} className="mx-auto mb-2 opacity-30" />
          {texts[tab]}
        </div>
      );
    }
    return (
      <>
        {pagedList.map(m => (
          <MessageRow
            key={m.id}
            msg={m}
            isInbox={tab === 'important' ? !!m.is_inbox : isInboxTab}
            onClick={() => openMessage(m)}
            isAdmin={isAdmin}
            selected={selectedIds.includes(m.id)}
            onSelect={toggleSelect}
            onToggleImportant={handleToggleImportant}
            showDirection={tab === 'important'}
          />
        ))}
        <Pagination total={currentList.length} page={safePage} onPageChange={setPage} />
      </>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="messages-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2B4B8C]">G&M Mail</h1>
          <p className="text-[#2B4B8C]/60 text-sm">Mensajeria interna</p>
        </div>
        <Button onClick={openCompose} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="compose-btn">
          <PencilSimple size={16} className="mr-1.5" /> Nuevo Mensaje
        </Button>
      </div>

      <div className="relative" data-testid="search-bar">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/30" />
        <Input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); setSelectedIds([]); }}
          placeholder="Buscar por usuario, empresa, mandante, asunto..."
          className="pl-9 pr-8 border-[#2B4B8C]/10 bg-white h-9 text-sm"
          data-testid="search-input"
        />
        {search && (
          <button onClick={() => { setSearch(''); setPage(1); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#2B4B8C]/30 hover:text-[#2B4B8C]/60" data-testid="search-clear">
            <X size={14} />
          </button>
        )}
      </div>

      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-0">
          {selectedMsg ? (
            <div className="p-5">
              <MessageDetail
                msgId={selectedMsg}
                onBack={() => setSelectedMsg(null)}
                onReplySent={fetchMessages}
                currentUserId={user?.id}
                isAdmin={isAdmin}
                onToggleImportant={handleToggleImportant}
                onDelete={handleDeleteSingle}
              />
            </div>
          ) : (
            <Tabs value={tab} onValueChange={(v) => { setTab(v); setSelectedIds([]); setPage(1); }}>
              <div className="border-b border-[#2B4B8C]/10 px-4 pt-3 flex items-center justify-between">
                <TabsList className="bg-transparent border-0 p-0 h-auto gap-4">
                  <TabsTrigger value="inbox" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[#2B4B8C] data-[state=active]:border-b-2 data-[state=active]:border-[#2BBAA4] rounded-none pb-2.5 px-1 text-sm" data-testid="tab-inbox">
                    <EnvelopeSimple size={16} className="mr-1.5" /> Bandeja de entrada
                    {unreadCount > 0 && <Badge className="ml-1.5 bg-red-500 text-white border-0 text-[10px] h-5 min-w-5 flex items-center justify-center">{unreadCount}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="sent" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[#2B4B8C] data-[state=active]:border-b-2 data-[state=active]:border-[#2BBAA4] rounded-none pb-2.5 px-1 text-sm" data-testid="tab-sent">
                    <PaperPlaneTilt size={16} className="mr-1.5" /> Enviados
                  </TabsTrigger>
                  <TabsTrigger value="important" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[#2B4B8C] data-[state=active]:border-b-2 data-[state=active]:border-[#2BBAA4] rounded-none pb-2.5 px-1 text-sm" data-testid="tab-important">
                    <Star size={16} className="mr-1.5" /> Importantes
                    {importantCount > 0 && <Badge className="ml-1.5 bg-amber-400 text-black border-0 text-[10px] h-5 min-w-5 flex items-center justify-center">{importantCount}</Badge>}
                  </TabsTrigger>
                </TabsList>
                {currentList.length > 0 && (
                  <div className="flex items-center gap-2 pb-2">
                    <Checkbox
                      checked={selectedIds.length === pagedList.length && pagedList.length > 0}
                      onCheckedChange={selectAll}
                      className="border-[#2B4B8C]/20 data-[state=checked]:bg-[#2BBAA4] data-[state=checked]:border-[#2BBAA4]"
                      data-testid="select-all-checkbox"
                    />
                    <span className="text-[10px] text-[#2B4B8C]/40">Todos</span>
                    {selectedIds.length > 0 && (
                      <Button variant="ghost" size="sm" onClick={handleBulkDelete} className="h-7 px-2 text-red-500 hover:text-red-600 hover:bg-red-50" data-testid="bulk-delete-btn">
                        <Trash size={14} className="mr-1" /> Eliminar ({selectedIds.length})
                      </Button>
                    )}
                  </div>
                )}
              </div>
              <TabsContent value="inbox" className="mt-0">
                {renderList(pagedList, true)}
              </TabsContent>
              <TabsContent value="sent" className="mt-0">
                {renderList(pagedList, false)}
              </TabsContent>
              <TabsContent value="important" className="mt-0">
                {renderList(pagedList, false)}
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2B4B8C]">Nuevo Mensaje</DialogTitle>
            <DialogDescription className="sr-only">Redactar un nuevo mensaje</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-[#2B4B8C]/60 mb-1 block">Para</label>
              <Select value={form.recipient_id} onValueChange={v => setForm(f => ({ ...f, recipient_id: v }))}>
                <SelectTrigger className="border-[#2B4B8C]/15" data-testid="recipient-select">
                  <SelectValue placeholder="Seleccionar destinatario" />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} {c.company_name ? `(${c.company_name})` : ''} - {c.role === 'admin' ? 'Admin' : 'Contratista'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#2B4B8C]/60 mb-1 block">Asunto</label>
              <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Asunto del mensaje" className="border-[#2B4B8C]/15" data-testid="subject-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#2B4B8C]/60 mb-1 block">Mensaje</label>
              <Textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} placeholder="Escribe tu mensaje..." rows={5} className="border-[#2B4B8C]/15 resize-none" data-testid="body-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setComposeOpen(false)} className="flex-1 border-[#2B4B8C]/10">Cancelar</Button>
              <Button onClick={handleSend} disabled={sending} className="flex-1 bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="send-btn">
                <PaperPlaneTilt size={16} className="mr-1.5" /> {sending ? 'Enviando...' : 'Enviar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#2B4B8C] flex items-center gap-2">
              <Warning size={20} className="text-red-500" /> Confirmar eliminacion
            </DialogTitle>
            <DialogDescription className="text-[#2B4B8C]/60 pt-2">
              {deleteConfirm?.ids.length === 1
                ? 'Este mensaje sera eliminado de tu bandeja. Esta accion no se puede deshacer.'
                : `Se eliminaran ${deleteConfirm?.ids.length} mensajes de tu bandeja. Esta accion no se puede deshacer.`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1 border-[#2B4B8C]/10">Cancelar</Button>
            <Button onClick={confirmDelete} className="flex-1 bg-red-500 text-white hover:bg-red-600" data-testid="confirm-delete-btn">
              <Trash size={14} className="mr-1.5" /> Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
